import { Component, type ReactElement } from 'react';
import flow from 'lodash/flow';
import {
	type AnalyticsEventPayload,
	withAnalyticsEvents,
	type UIAnalyticsEvent,
} from '@atlaskit/analytics-next';
import { getOpsgenieInstanceUrl } from '@atlassian/jira-cross-flow-utils/src/services/index.tsx';
import type FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import messages from './messages';
import type { ServiceResponse } from './types';
import { getScheduleByTeam } from './utils';

export type States = {
	loading: boolean;
	error: FetchError | undefined | null;
	data?: ServiceResponse;
};

export type Props = {
	baseUrl: string;
	cloudId: string;
	createAnalyticsEvent: (data: AnalyticsEventPayload) => UIAnalyticsEvent;
	teamId: string;
	children: (arg1: States) => ReactElement;
	intl: IntlShape;
};

// eslint-disable-next-line jira/react/no-class-components
class SchedulesService extends Component<Props, States> {
	state = {
		loading: false,
		error: undefined,
		data: undefined,
	};

	componentDidMount() {
		this.fetchData();
	}

	async fetchData() {
		const { intl, baseUrl, cloudId, teamId, createAnalyticsEvent } = this.props;
		const unknownUserName = intl.formatMessage(messages.unknownUserName);

		this.setState({ loading: true, data: undefined, error: undefined });

		let instanceUrl = null;

		try {
			instanceUrl = await getOpsgenieInstanceUrl();
			const schedules = await getScheduleByTeam(
				unknownUserName,
				baseUrl,
				cloudId,
				teamId,
				createAnalyticsEvent({}),
			);

			const numOfRotations = schedules.reduce(
				(acum, schedule) => acum + schedule.finalTimeline.rotations.length,
				0,
			);

			const data: ServiceResponse = {
				schedules,
				numOfRotations,
				instanceUrl,
			};

			this.setState({ loading: false, data, error: undefined });
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (e: any) {
			const data: ServiceResponse = {
				schedules: [],
				numOfRotations: 0,
				instanceUrl,
			};
			this.setState({ loading: false, data, error: e });
		}
	}

	render() {
		const { children } = this.props;
		const { loading, error, data } = this.state;

		return children({
			loading,
			data,
			error,
		});
	}
}

export default flow(injectIntl, withAnalyticsEvents())(SchedulesService);

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { useOpsgenieSchedules } from './opsgenie-schedules';
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type { OgSchedulesState, TeamSchedules } from './opsgenie-schedules';
